import { LegalRestriction, Treatment } from '@next/arable-lib/advice';
import { ProductTypeEnum, StateOfMatterEnum, UnitOfMeasurementEnum } from '@next/arable-lib/crop-aid';
import { CropVariety } from '@next/arable-lib/crop';
import { RootState } from '@next/core-lib/app';
import { SessionState } from '@next/core-lib/session';

import { CropFieldNutrientCalculations } from '../bem/shared/models/models';
import {
  BlightChartDetail,
  BlightData,
  CropFieldRegistration,
  CustomerBlightRights,
  ParticipatingCropField,
} from '../blight-v2/models/models';
import { DownyMildewData } from '../downy-mildew/models/models';

// add your interfaces for the api responses and state here
// SiteState is the root state of your app module
export interface SiteState extends RootState {
  app: AppState;
}

export interface ArableSessionState extends SessionState {
  arable?: {
    cropYear?: number | null;
  };
}

export interface AppState {
  blightData: BlightData[];
  blightParticipatingCropFields: ParticipatingCropField[];
  blightChartDetails: BlightChartDetail[] | null;
  blightRecalculationStatusPerCropField: BlightRecalculationStatusPerCropField[];
  downyMildewData: DownyMildewData[];
  downyMildewRecalculationStatusPerCropField: DownyMildewRecalculationStatusPerCropField[];
  showRecalculationFinishedNotification: boolean;
  blightAdviceDate: Date;
  newBlightDataForCropField: NewBlightDataForCropField;
  customerBlightRights: CustomerBlightRights | null;
  basicSprays: BasicSpray[];
  sprayApplications?: SprayApplication[];
  isSpraysLoading: boolean;
  shouldRecalculateForCropFieldIds: number[];
  shouldGetNewBlightData: boolean;
  cropFieldRegistration: CropFieldRegistration | null;
  selectedCropField: ParticipatingCropField | null;
  selectedBlightCropFieldIds: number[];
  cropFieldsIdsForSpray?: number[];
  legalRestrictions?: LegalRestriction[];
}

export interface ApiErrorResponse {
  statusCode: number;
  message: string;
  validationMessageCodes?: string[] | null;
}

export interface ApiNotFoundErrorResponse {
  type: string;
  title: string;
  status: number;
  traceId: string;
}

export enum SyncStatus {
  syncing = 'Syncing',
  synced = 'Synced',
  syncInitialized = 'SyncInitialized',
  awaitingBlightCalculation = 'AwaitingBlightCalculation',
  blightCalculationDone = 'BlightCalculationDone',
  failedToSync = 'FailedToSync',
  none = 'None',
  mock = 'mock',
}

export interface Spray {
  id: string;
  fracGroup: string[];
  date: Date;
  fungicides: string;
  maxApplications: number;
}

export interface BasicSpray {
  id: string;
  name: string;
  code: string;
  protectionCategory: number;
  cropGrowthPhase: GrowthPhase;
  preferred: boolean;
  fracGroups: string[];
  maxApplications: number;
}

export interface CreateSprayApplicationData {
  sprayId: number;
  cropFieldId: number;
  cropYear: number;
  sprayedOn: Date;
}

export interface SprayApplicationResponse {
  sprayApplications: SprayApplication[];
}

export interface SprayApplication {
  id: number;
  sprayId: number;
  cropFieldId: number;
  name: string;
  sprayedOn: Date;
}

export interface SprayApplicationWithIndex extends SprayApplication {
  sprayIndex: number;
}

export interface GroupedSprays {
  cropFieldId: number;
  cropFieldName: string;
  sprays: SprayApplicationWithIndex[];
}

export interface EligibleCropFieldsForSprayApplicationsResponse {
  cropFieldIds: number[];
}

export interface GroupedTreatments {
  cropFieldId: number;
  cropFieldName: string;
  treatments: Treatment[];
}

export enum GrowthPhase {
  none = 'None',
  phase1 = 'Phase1',
  phase2 = 'Phase2',
  phase3 = 'Phase3',
}

export enum SprayTypes {
  NoAdvice = -1,
  CropProtected = 0,
  PreventiveSpray = 1,
  CurativeSpray = 2,
  StopSpray = 3,
}

export enum PotatoCropType {
  seedPotato = 'SeedPotato',
  wareOrStarchPotato = 'WareOrStarchPotato',
  unknown = 'Unknown',
}

export interface BlightRecalculationStatusPerCropField {
  cropFieldId: number;
  isBlightRecalculating: boolean;
}

export interface DownyMildewRecalculationStatusPerCropField {
  cropFieldId: number;
  isDownyMildewRecalculating: boolean;
}

export interface NewBlightDataForCropField {
  cropFieldId: number;
  timestamp: Date;
}

export class ManualCrop {
  id: number | undefined;
  cropId: number | undefined;
  cropName?: string;
  areaInHectares: number | undefined;
  soilTypeId: number | undefined;
  soilTypeName: string | undefined;
  name: string | undefined;
  province: string | undefined;
  cropVarietyId?: number;
  cropVarietyName?: string;
  organic = false;
  isParticipatingToBem: boolean | undefined;
  planetProof = false;
  groundwaterPreservation = false;
}

export interface CropVarietiesByCropId {
  cropId: number;
  cropVarieties: CropVariety[];
}

export interface GetCropVarietiesResponse {
  cropVarieties: CropVariety[];
}

export enum Province {
  Drenthe = 'Drenthe',
  Flevoland = 'Flevoland',
  Friesland = 'Friesland',
  Gelderland = 'Gelderland',
  Groningen = 'Groningen',
  Limburg = 'Limburg',
  NoordBrabant = 'Noord-Brabant',
  NoordHolland = 'Noord-Holland',
  Overijssel = 'Overijssel',
  Utrecht = 'Utrecht',
  Zeeland = 'Zeeland',
  ZuidHolland = 'Zuid-Holland',
}

export interface CustomerOrderPlanResponse {
  result: CustomerOrderPlan[];
}

export interface CustomerOrderPlan {
  cropAidId: number;
  cropAidName: string;
  orderLineIds: number[];
  packageId: number | null;
  unitOfMeasurement: string;
  customerProductInventoryAmount: number;
  orderLinesTotalAmount: number;
  requisiteAmount: number;
  packageAmount: number | null;
  isMainCropAid: boolean | null;
  isPackage: boolean | null;
  result: number;
}

export interface CropAidResponse {
  id: number;
  productId?: number;
  productV2Id?: number;
  name: string;
  isActive: boolean;
  unitOfMeasurement: string;
  cropProtection?: CropProtectionData;
  source: number; // 0=OneSAP, 1=Agrifirm, 2=Customer
  productType?: string;
  indicativePrice?: number;
  isCropProtection?: boolean;
  isFertilizer?: boolean;
  stateOfMatter?: string;
  isNutrition?: boolean;
  forbiddenForPlanetProof?: boolean;
  isHidden?: boolean;
}

export interface CropProtectionData {
  admissions: Admission[];
  isPhytophthora?: boolean;
  isDownyMildew?: boolean;
  isVirusMonitor?: boolean;
  activeIngredient: string;
  admissionNumbers: string;
}

export interface Admission {
  id: number;
  number: string;
  activeIngredients: AdmissionIngredient[];
}

export interface AdmissionIngredient {
  ingredientId: number;
  name: string;
  dosage: number;
}

export enum SourceEnum {
  Manual = 'Manual',
  AgrifirmArableLegacySAP = 'AgrifirmArableLegacySAP',
  OneSAP = 'OneSAP',
}

export interface GetCropAidsResponse {
  cropAids: CropAidResponse[];
}

export interface ArticleName {
  title: string;
  description: string;
  language: string;
}

export interface CropAidPackage {
  id: number;
  packageId: number;
  name: string;
  articleNumber: string;
  productType: string;
  articleNames: ArticleName[];
  unitOfMeasurement: string;
  amount: number;
}

export interface GetCropAidPackagesResponse {
  cropAids: CropAidPackage[];
}

export interface GetPackagesResponse {
  packages: Package[];
}

export interface CropAid {
  id: number;
  productId: number;
  name: string;
  unitOfMeasurement: UnitOfMeasurementEnum;
  codes: string[];
  isActive: boolean;
  cropProtection?: CropProtectionData;
  source: SourceEnum;
  // Additional properties required by @next/arable-lib/crop-aid
  productType: ProductTypeEnum;
  indicativePrice: number;
  isCropProtection: boolean;
  isFertilizer: boolean;
  stateOfMatter: StateOfMatterEnum;
  isNutrition: boolean;
  forbiddenForPlanetProof: boolean;
  isHidden: boolean;
}

export interface OrderPlanCropAidDisplayData {
  cropAid: CropAid;
  customerProductInventoryAmount: number;
  orderLinesTotalAmount: number;
  requisiteAmount: number;
}

export interface ProductStockData {
  cropAidId: number;
  amount: number;
}

export interface UpsertCustomerProductStockRequest {
  year: number;
  productInventories: ProductStockData[];
}

export interface SubmittedProductStockData extends ProductStockData {
  name: string;
  admissionNumbers: string[];
  unitOfMeasurement: string;
}

export interface SubmittedProductInventoryResponse {
  productInventories: SubmittedProductStockData[];
}

export interface UpsertSubmittedProductInventoryRequest {
  productInventories: ProductStockData[];
}

export interface FertilizerStockData {
  cropAidId: number;
  stock: number;
}

export interface UpsertFertilizerStockRequest {
  cropYear: number;
  fertilizerStock: FertilizerStockData[];
}

export interface PeriodOrderPlanWithDetails {
  periodId: number;
  periodName: string;
  startDispatchFrom: string | Date;
  deliverBefore: string | Date;
  cropAidId: number;
  cropAidName: string;
  packageId: number;
  packageCount?: number;
  packageAmount: number;
  packageUnitOfMeasurement: string;
  requisiteAmount: number;
}

export interface OrderplanCropAidDisplayData {
  cropAid: CropAid;
  customerProductInventoryAmount: number;
  orderLinesTotalAmount: number;
  requisiteAmount: number;
}

export enum AllowedOrderRoles {
  None = 'None',
  All = 'All',
  AdvisorAndAbove = 'AdvisorAndAbove',
}

export interface Package {
  id: number;
  unitOfMeasurement: string;
  productId: number;
  amount: number;
  isActive: boolean;
  name: string;
  packageVersion: PackageVersion;
  allowedUsers: AllowedOrderRoles;
}

export enum PackageVersion {
  V1 = 'V1',
  V2 = 'V2',
}

export interface OrderLine {
  id: number;
  packageId: number;
  cropAidId?: number;
  count: number;
  year: number;
  periodId: number;
  status: OrderLineStatus;
  deliveryPeriodMoment: DeliveryPeriodMomentType;
  treatmentType: TreatmentTypes;
  orderedDate: string;
  package?: Package;
  cropAid?: CropAid;
  shipToId: number;
  addedByOptimizer: boolean;
  /**
   * Newest version (2022) of tracking orderlines to the cart-service.
   */
  cartId?: number;
  /**
   * Old version (~2020-2021) of tracking orderlines to the checkout-service.
   */
  checkoutId?: number;
  /**
   * Oldest version (~2020) of tracking orderlines to the order-management-service.
   */
  orderedId?: number;
}

export enum OrderLineStatus {
  open = 'Open',
  inCart = 'InCart',
  finalized = 'Finalized',
}

export enum DeliveryPeriodMomentType {
  empty = 'Empty',
  beginning = 'Beginning',
  middle = 'Middle',
  end = 'End',
}

export enum TreatmentTypes {
  GBM = 'GBM',
  Fertilization = 'Fertilization',
}

export interface GetOrderLinesResponse {
  orderLines: OrderLine[];
}

export interface CropAidFinalStatus {
  cropAidName: string | undefined;
  cropAidId: number;
  quantity: number;
}

export interface GetAutomaticAdviceResponse {
  customerId: number;
  cropYear: number;
  state: 'Triggered' | 'AwaitingOrderplan' | 'ReadyToOrder' | 'NoCropFieldsFound' | 'CropFieldsAlreadyExist';
  cropFieldIds: number[];
  completedCropFieldIds: number[];
}

export interface GetCompanySettingsResponse {
  customerId: number;
  driftReduction: number;
  yearlySettings: {
    year: number;
    nitrogenCuts: {
      cutsInHaPeriod1: number;
      cutsInHaPeriod2: number;
      cutsInHaPeriod3: number;
    };
  };
  defaultSoilTypeId: number;
  automaticAdvice: boolean | null;
}

export class BFPState {
  cropFieldNutrientCalculation?: CropFieldNutrientCalculations[];

  constructor(cropFieldNutrientCalculation?: CropFieldNutrientCalculations[]) {
    this.cropFieldNutrientCalculation = cropFieldNutrientCalculation;
  }
}

export interface FertilizerOrderPlanResponse {
  orderableProducts: OrderableProduct[];
  nonOrderableProducts: OrderableProduct[];
}

export interface OrderableProduct {
  orderLineId: number;
  cropAidId: number;
  productName: string;
  cropAidWithArticles: CropAidWithArticles;
  amount: number;
  packageId: number;
  requisiteAmount: number;
  stockAmount: number;
  orderedAmount: number;
  toOrderAmount: number;
  result: number;
  packageUnit: string | null;
  cropAidUnit: string;
  minimumOrderUnitQuantity: number | null;
  maximumOrderUnitQuantity: number | null;
  increaseOrderUnitQuantity: number | null;
}

export interface CropAidWithArticles {
  id: number;
  productId: number | null;
  productV2Id: number | null;
  packageId: number;
  name: string;
  articleNumber: string;
  additionalLanguages: AdditionalLanguage[];
  unitOfMeasurement: string;
  amount: number;
}

export interface AdditionalLanguage {
  title: string;
  description: string;
  language: string;
}

export interface CreateOrderRequest {
  year: number;
  preferredOrderDate: Date | null;
}

export interface CreateOrderResponse {
  id: number;
}

export enum PeriodTypeEnum {
  GBM = 'GBM',
  Horticulture = 'Horticulture',
  Fertilization = 'Fertilization',
}

export interface SalesOrderPeriod {
  id: number;
  year: number;
  periodType: PeriodTypeEnum;
  periodNumber: number;
  name: string;
  orderReceivedBefore: Date;
  startDispatchFrom: Date;
  deliverBefore: Date;
}

export interface GetSalesOrderPeriodsResponse {
  salesOrderPeriods: SalesOrderPeriod[];
}

export interface OrderPlanV2 {
  cropAidId: number;
  cropAidName: string;
  orderLineIds: number[];
  packageId: number | null;
  unitOfMeasurement: string;
  customerProductInventoryAmount: number;
  orderLinesTotalAmount: number;
  requisiteAmount: number;
  packageAmount: number | null;
  isMainCropAid: boolean;
  isPackage: boolean;
  result: number;
  year: number;
  orderplanPeriods: OrderPlanPeriod[];
}

export interface OrderPlanPeriod {
  periodId: number;
  periodName: string;
  packageCount: number | null;
  requisiteAmount: number;
  orderLines: OrderLineData[];
  orderLinesTotalAmount: number;
  isDisabled: boolean;
}

export interface OrderLineData {
  id: number;
  count: number;
  status: string;
  addedByOptimizer: boolean;
  deliveryPeriodMoment: string;
  shipToId: number;
  checkoutId: number | null;
  cartId: number | null;
  orderedId: number | null;
  orderedDate: string | null;
}
